<template>
    <div class="rounded-lg border border-gray-200 bg-white p-4">
        <div class="mb-3 flex flex-wrap items-start gap-2">
            <div class="min-w-0 flex-1">
                <h3 class="text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                    {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.title') }}
                </h3>
                <p class="mt-1 text-xs text-gray-500">{{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.description') }}</p>
            </div>
            <div class="ml-auto flex items-center gap-3">
                <p v-if="selectedPeriodRange" class="text-xs text-gray-400">{{ selectedPeriodRange }}</p>
                <button
                    v-if="suggestedStart"
                    type="button"
                    class="shrink-0 rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                    @click="emit('useSuggestedStart', suggestedStart)"
                >
                    {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.useSuggestedStart') }}
                </button>
            </div>
        </div>
        <div v-if="hasError" class="py-8 text-center">
            <p class="text-xs text-red-700">{{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.loadFailed') }}</p>
            <button type="button" class="mt-2 text-xs font-semibold text-primary-700 hover:text-primary-800" @click="emit('retry')">
                {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.retry') }}
            </button>
        </div>
        <div v-else-if="isLoading" class="flex items-center justify-center py-16">
            <Spinner class="h-6 w-6" />
        </div>
        <GenericChart v-else-if="tally.length" :options="chartOptions" :expandable="false" />
        <p v-else class="py-8 text-center text-xs italic text-gray-400">
            {{ $t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.empty') }}
        </p>
        <p v-if="!suggestedStart && !isLoading && tally.length" class="mt-2 text-xs text-gray-500">
            {{
                $t(
                    suggestionConflicts
                        ? 'views.monitoring.productionGroups.modals.manageProductionGroup.timeline.suggestedStartConflicts'
                        : 'views.monitoring.productionGroups.modals.manageProductionGroup.timeline.noSuggestedStart'
                )
            }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import merge from 'lodash-es/merge';
import type { Options } from 'highcharts';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { ChartType, type ProductionGroup } from '@/types';
import type { PigTallyDay } from '@/types/SectionLiveKpi';
import { formatChartTooltipHeader } from './chartTooltipHeader';
import type { ProductionGroupTimelineRange } from './productionGroupTimeline';
import { productionGroupIntersectsTimeline } from './productionGroupTimeline';

const props = defineProps<{
    tally: PigTallyDay[];
    productionGroups: ProductionGroup[];
    range: ProductionGroupTimelineRange;
    formStart: string;
    formEnd: string;
    editedProductionGroupId: number | null;
    suggestedStart: string | null;
    suggestionConflicts: boolean;
    isLoading: boolean;
    hasError: boolean;
}>();

const emit = defineEmits<{
    useSuggestedStart: [start: string];
    retry: [];
}>();

const stockDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const selectedPeriodRange = computed(() => {
    if (!props.formStart) return null;

    const start = i18n.global.d(Date.parse(props.formStart), 'short');
    const end = props.formEnd ? i18n.global.d(Date.parse(props.formEnd), 'short') : null;
    return end ? `${start} – ${end}` : start;
});

const plotBands = computed(() => {
    const priorPeriods = props.productionGroups
        .filter((productionGroup) => productionGroup.id !== props.editedProductionGroupId && productionGroupIntersectsTimeline(productionGroup, props.range))
        .map((productionGroup) => ({
            from: Math.max(Date.parse(productionGroup.start), Date.parse(props.range.from)),
            to: Math.min(Date.parse(productionGroup.end ?? props.range.to), Date.parse(props.range.to)),
            color: 'rgba(156, 163, 175, 0.16)',
            label: {
                text: productionGroup.name,
                verticalAlign: 'top',
                y: 12,
                style: { color: '#6B7280', fontSize: '10px', fontWeight: '500' },
            },
        }));

    if (!props.formStart) return priorPeriods;

    return [
        ...priorPeriods,
        {
            from: Math.max(Date.parse(props.formStart), Date.parse(props.range.from)),
            to: Math.min(Date.parse(props.formEnd || props.range.to), Date.parse(props.range.to)),
            color: 'rgba(37, 99, 235, 0.12)',
        },
    ];
});

const chartOptions = computed(() => {
    const base: Options = merge({}, stockDefaults.value);

    return merge(base, {
        chart: {
            height: 270,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: true, height: 28, margin: 8 },
        legend: { enabled: false },
        xAxis: {
            min: Date.parse(props.range.from),
            max: Date.parse(props.range.to),
            tickInterval: null,
            plotBands: plotBands.value,
            plotLines: props.suggestedStart
                ? [
                      {
                          value: Date.parse(props.suggestedStart),
                          color: '#2563EB',
                          dashStyle: 'Dash',
                          width: 1,
                          zIndex: 4,
                          label: {
                              text: i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.suggestedStart') as string,
                              rotation: 0,
                              y: 12,
                              style: { color: '#2563EB', fontSize: '10px' },
                          },
                      },
                  ]
                : [],
        },
        yAxis: {
            softMin: 0,
            allowDecimals: false,
        },
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatChartTooltipHeader(this.x)}</span>`;
                const point = this.points?.[0];
                return `${header}<br/><span style="color:${point?.color}">●</span> ${point?.series?.name}: <b>${i18n.global.n(Math.round(point?.y ?? 0))}</b>`;
            },
        },
        series: [
            {
                type: 'line',
                name: i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.timeline.pigTally') as string,
                data: props.tally.map((point) => [Date.parse(point.date), point.pigs]),
                marker: { enabled: false },
                dataGrouping: { enabled: false },
            },
        ],
    });
});
</script>
